/**
 * The Data Room archive: every soft-deleted document, who deleted it and
 * when, with the way back — Restore document — right on the row, and the
 * document's own page one click away for anyone who wants to read it first.
 *
 * Reachable only through the Data Room root's quiet link, and only for the
 * roles the Data Room admits at all; the server answers everyone else with
 * the standard not-available page, existence unhinted.
 */

import { useCallback, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import {
  ApiFailure,
  fetchDataRoom,
  newIdempotencyKey,
  recoverDataRoomDocument,
  type DataRoomDeletedDocumentSummary,
} from '../api.js';
import { EventTime, useWorkspaceCopy } from '../components.js';
import { useSession } from '../session.js';
import { workspaceSlots } from '../slots.js';
import { Button, ConfirmDialog, EmptyState, InlineAlert, useToasts } from '../ui.js';
import { FailurePanel, LoadingPanel, PageTitle, useAsync } from './common.js';
import { NotAvailablePage } from './NotAvailablePage.js';

export function DataRoomDeletedPage(): ReactNode {
  const copy = useWorkspaceCopy();
  const { session } = useSession();
  const { announce } = useToasts();

  const load = useCallback(() => fetchDataRoom(), []);
  const { state, reload } = useAsync(load);

  const [target, setTarget] = useState<DataRoomDeletedDocumentSummary | null>(null);
  const [recoverKey, setRecoverKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<ApiFailure | null>(null);

  const recover = (): void => {
    if (target === null) return;
    setBusy(true);
    setFailure(null);
    recoverDataRoomDocument(recoverKey, target.id, {
      expectedVersion: target.version,
    })
      .then(() => {
        setBusy(false);
        setTarget(null);
        announce(copy.dataRoom.toastDocumentRecovered);
        reload();
      })
      .catch((thrown: unknown) => {
        setBusy(false);
        setFailure(thrown instanceof ApiFailure ? thrown : new ApiFailure('failed'));
      });
  };

  if (session.status === 'authenticated' && !session.profile.mayUseDataRoom) {
    return <NotAvailablePage />;
  }
  if (state.status === 'loading') return <LoadingPanel />;
  if (state.status === 'failed') {
    if (state.failure.kind === 'forbidden' || state.failure.kind === 'missing') {
      return <NotAvailablePage />;
    }
    return <FailurePanel failure={state.failure} onRetry={reload} />;
  }

  const deleted = state.value.deletedDocuments;

  return (
    <>
      <nav
        aria-label={copy.dataRoom.breadcrumbLabel}
        style={{ marginBottom: 'var(--space-sm)' }}
      >
        <ol
          style={{
            listStyle: 'none',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 6,
            padding: 0,
            margin: 0,
            font: 'var(--type-body-md)',
          }}
        >
          <li>
            <Link to="/data-room" style={{ color: 'var(--text-link)' }}>
              {copy.dataRoom.title}
            </Link>
          </li>
          <li style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <span aria-hidden="true" style={{ color: 'var(--text-muted)' }}>
              /
            </span>
            <span aria-current="page" style={{ color: 'var(--text-primary)' }}>
              {copy.dataRoom.deletedDocuments}
            </span>
          </li>
        </ol>
      </nav>

      <PageTitle>{copy.dataRoom.deletedDocuments}</PageTitle>

      {deleted.length === 0 ? (
        <EmptyState title={copy.dataRoom.deletedEmpty} />
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="mj-table">
            <thead>
              <tr>
                <th scope="col">{copy.dataRoom.columns.title}</th>
                <th scope="col">{copy.dataRoom.categoryLabel}</th>
                <th scope="col">{copy.dataRoom.deletedByLabel}</th>
                <th scope="col"></th>
              </tr>
            </thead>
            <tbody>
              {deleted.map((document) => (
                <tr key={document.id}>
                  <td>
                    <span
                      style={{
                        display: 'inline-flex',
                        gap: 8,
                        alignItems: 'baseline',
                        flexWrap: 'wrap',
                      }}
                    >
                      <Link
                        to={`/data-room/${document.id}`}
                        style={{ color: 'var(--text-link)' }}
                      >
                        {document.title}
                      </Link>
                      {workspaceSlots().documentBadge?.({
                        audience: document.audience,
                      })}
                    </span>
                  </td>
                  <td>
                    {copy.dataRoom.categories[document.category] ?? document.category}
                  </td>
                  <td>
                    <span style={{ display: 'inline-flex', gap: 8, flexWrap: 'wrap' }}>
                      <span>{document.deletedBy.displayName}</span>
                      <EventTime iso={document.deletedAt} />
                    </span>
                  </td>
                  <td>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setTarget(document);
                        setRecoverKey(newIdempotencyKey());
                        setFailure(null);
                      }}
                    >
                      {copy.dataRoom.recoverDocument}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        open={target !== null}
        title={copy.dataRoom.recoverTitle}
        body={target === null ? '' : copy.dataRoom.recoverBody(target.title)}
        whatHappensLabel={copy.dialogs.whatHappens}
        effects={copy.dataRoom.recoverEffects}
        confirmLabel={copy.dataRoom.recoverDocument}
        cancelLabel={copy.actions.cancel}
        busy={busy}
        error={
          failure === null ? undefined : (
            <InlineAlert
              tone="error"
              title={
                failure.kind === 'conflict'
                  ? copy.states.conflictTitle
                  : copy.states.saveFailedTitle
              }
              role="alert"
            >
              {failure.kind === 'conflict'
                ? copy.dataRoom.conflictBody
                : (failure.detail ?? copy.states.saveFailedBody)}
            </InlineAlert>
          )
        }
        onConfirm={recover}
        onCancel={() => {
          if (!busy) setTarget(null);
        }}
      />
    </>
  );
}
